import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import {
    ShieldCheck,
    AlertCircle,
    Calendar,
    FileText,
    Gavel,
    ChevronDown,
    CheckCircle,
    Edit2,
} from "lucide-react";

const CriminalitySecurityForm = ({ form, preview, handleChange, handleDetailChange, loading, setPreview, onSave }) => {
    const [expandedSections, setExpandedSections] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    const toggleSection = (field) => {
        setExpandedSections(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const validateForm = () => {
        const newErrors = {};
        const fields = ['committedCrime', 'arrestedForOffence', 'chargedForOffence', 'convictedForOffence'];

        fields.forEach(field => {
            if (form[field]) {
                const details = form.criminalDetails?.[field];
                if (!details?.date) newErrors[`${field}_date`] = "Event date is required";
                if (!details?.reason) newErrors[`${field}_reason`] = "Reason details are required";
                if (!details?.result) newErrors[`${field}_result`] = "Legal outcome is required";
            }
        });

        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in preview mode
    if (preview && !isEditing) {
        return (
            <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <ShieldCheck size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Security Declaration</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Background & Legal Verification</p>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Security declaration verified and approved</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                        <PreviewCard
                            label="Committed Crime"
                            value={preview.committedCrime}
                            data={preview.criminalDetails?.find(d => d.type === "committedCrime")}
                        />
                        <PreviewCard
                            label="Arrest Record"
                            value={preview.arrestedForOffence}
                            data={preview.criminalDetails?.find(d => d.type === "arrestedForOffence")}
                        />
                        <PreviewCard
                            label="Legal Charges"
                            value={preview.chargedForOffence}
                            data={preview.criminalDetails?.find(d => d.type === "chargedForOffence")}
                        />
                        <PreviewCard
                            label="Prior Convictions"
                            value={preview.convictedForOffence}
                            data={preview.criminalDetails?.find(d => d.type === "convictedForOffence")}
                        />
                    </div>

                    {(preview?.status === "reject" || !preview?.status) && (
                        <div className="flex justify-center pt-6 mt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
                            >
                                <Edit2 size={16} /> Edit Declaration
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Processing declaration...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <ShieldCheck size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Security Declaration</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Background & Legal Verification</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {Object.keys(localErrors).length > 0 && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">


                    <div className="space-y-6 sm:space-y-8">
                        <QuestionBlock
                            label="Have you ever committed a criminal offence?"
                            field="committedCrime"
                            value={form.committedCrime}
                            details={form.criminalDetails?.committedCrime}
                            onChange={handleChange}
                            onDetailChange={handleDetailChange}
                            isExpanded={expandedSections.committedCrime}
                            onToggle={() => toggleSection('committedCrime')}
                            errors={localErrors}
                        />

                        <QuestionBlock
                            label="Have you ever been arrested for an offence?"
                            field="arrestedForOffence"
                            value={form.arrestedForOffence}
                            details={form.criminalDetails?.arrestedForOffence}
                            onChange={handleChange}
                            onDetailChange={handleDetailChange}
                            isExpanded={expandedSections.arrestedForOffence}
                            onToggle={() => toggleSection('arrestedForOffence')}
                            errors={localErrors}
                        />

                        <QuestionBlock
                            label="Have you ever been charged with an offence?"
                            field="chargedForOffence"
                            value={form.chargedForOffence}
                            details={form.criminalDetails?.chargedForOffence}
                            onChange={handleChange}
                            onDetailChange={handleDetailChange}
                            isExpanded={expandedSections.chargedForOffence}
                            onToggle={() => toggleSection('chargedForOffence')}
                            errors={localErrors}
                        />

                        <QuestionBlock
                            label="Have you ever been convicted of an offence?"
                            field="convictedForOffence"
                            value={form.convictedForOffence}
                            details={form.criminalDetails?.convictedForOffence}
                            onChange={handleChange}
                            onDetailChange={handleDetailChange}
                            isExpanded={expandedSections.convictedForOffence}
                            onToggle={() => toggleSection('convictedForOffence')}
                            errors={localErrors}
                        />
                    </div>


                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        {preview && (
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(false);
                                    setPreview(null);
                                }}
                                className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all"
                            >
                                Cancel
                            </button>
                        )}

                    </div>
                </form>
            </div>
        </div>
    );
};

/* REUSABLE FORM BLOCKS */
const QuestionBlock = ({ label, field, value, details, onChange, onDetailChange, isExpanded, onToggle, errors }) => (
    <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">
        {/* Question Header - Clickable on mobile */}
        <div
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-5 cursor-pointer md:cursor-default bg-gray-50/50"
            onClick={onToggle}
        >
            <label className="text-sm sm:text-base font-bold text-gray-800">
                {label}
            </label>
            <div className="flex items-center gap-2">
                <div className="flex bg-gray-200 p-1 rounded-lg w-fit">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange(field, false);
                        }}
                        className={`px-4 sm:px-5 py-1.5 rounded-md text-xs sm:text-sm font-bold transition-all ${!value ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}
                    >
                        NO
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange(field, true);
                        }}
                        className={`px-4 sm:px-5 py-1.5 rounded-md text-xs sm:text-sm font-bold transition-all ${value ? "bg-rose-500 text-white shadow-md" : "text-gray-500"}`}
                    >
                        YES
                    </button>
                </div>
                <button className="md:hidden text-gray-400">
                    <ChevronDown size={18} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
            </div>
        </div>

        {/* Details Section - Expandable */}
        {value && (
            <div className={`
                transition-all duration-300 overflow-hidden
                ${isExpanded !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
            `}>
                <div className="p-4 sm:p-5 pt-0 sm:pt-0 border-t border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-5">
                        <div className="md:col-span-4">
                            <label className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">
                                Event Date <span className="text-rose-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={details?.date || ""}
                                onChange={(e) => onDetailChange(field, "date", e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                                className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                    ${errors[`${field}_date`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                            />
                            {errors[`${field}_date`] && (
                                <p className="text-rose-500 text-[10px] mt-1">{errors[`${field}_date`]}</p>
                            )}
                        </div>
                        <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider block">
                                    Detailed Reason <span className="text-rose-500">*</span>
                                </label>
                                <textarea
                                    placeholder="Describe the incident in detail..."
                                    rows={3}
                                    value={details?.reason || ""}
                                    onChange={(e) => onDetailChange(field, "reason", e.target.value)}
                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm resize-none
                                        ${errors[`${field}_reason`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                />
                                {errors[`${field}_reason`] && (
                                    <p className="text-rose-500 text-[10px] mt-1">{errors[`${field}_reason`]}</p>
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider block">
                                    Legal Outcome <span className="text-rose-500">*</span>
                                </label>
                                <textarea
                                    placeholder="Final result, charges, penalties..."
                                    rows={3}
                                    value={details?.result || ""}
                                    onChange={(e) => onDetailChange(field, "result", e.target.value)}
                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm resize-none
                                        ${errors[`${field}_result`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                />
                                {errors[`${field}_result`] && (
                                    <p className="text-rose-500 text-[10px] mt-1">{errors[`${field}_result`]}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}
    </div>
);

/* PREVIEW CARD */
const PreviewCard = ({ label, value, data }) => {
    return (
        <div className={`relative flex flex-col rounded-xl sm:rounded-2xl border-2 transition-all p-4 sm:p-5 ${value ? "bg-gray-50/50 border-gray-200" : "bg-rose-50/20 border-rose-100 shadow-sm"}`}>
            <div className="flex justify-between items-start mb-3 sm:mb-4">
                <div className="flex-1">
                    <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                        {label.replace(/([A-Z])/g, ' $1')}
                    </p>
                    <p className={`text-base sm:text-lg font-bold leading-tight ${value ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {value ? "Record Declared" : "No Records"}
                    </p>
                </div>
                <div className={`p-2 sm:p-2.5 rounded-xl shrink-0 ${value ? 'bg-emerald-500 text-white shadow-sm' : 'bg-rose-500 text-white'}`}>
                    {value ? <ShieldCheck size={18} /> : <AlertCircle size={18} />}
                </div>
            </div>

            {value && data && (
                <div className="mt-3 pt-3 sm:pt-4 border-t border-green-100/50 space-y-3">
                    <div className="flex items-center gap-2 bg-white/80 w-fit px-2 py-1 rounded-lg border border-green-100/50">
                        <Calendar size={12} className="text-green-500" />
                        <span className="text-[10px] sm:text-xs font-semibold text-gray-700">
                            {data.date ? new Date(data.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                        <ScrollableInfo icon={<FileText size={12} />} label="Case Details" text={data.reason} />
                        <ScrollableInfo icon={<Gavel size={12} />} label="Legal Status" text={data.result} />
                    </div>
                </div>
            )}
        </div>
    );
};

const ScrollableInfo = ({ icon, label, text }) => (
    <div className="space-y-1">
        <p className="text-[9px] sm:text-[10px] font-bold text-emerald-600 uppercase flex items-center gap-1.5 tracking-wider">
            {icon} {label}
        </p>
        <div className="max-h-[70px] overflow-y-auto pr-1">
            <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed break-words whitespace-pre-wrap">
                {text || "N/A"}
            </p>
        </div>
    </div>
);

export default CriminalitySecurityForm;